import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CloseIcon from '@mui/icons-material/Close';

const HEADER_PX = 52;

/**
 * The explanation video in a dialog. The title and close button sit in a bar above the video and
 * the video keeps its 16:9 shape, so nothing covers the picture and there is no letterboxing.
 *
 * allowDownload: admin screens only. Students and parents (the default) can only play the video.
 */
export function VideoPlayerDialog({ open, onClose, videoUrl, title, allowDownload = false }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const blockSave = allowDownload ? undefined : (e) => e.preventDefault();
  const lockProps = allowDownload
    ? {}
    : {
        controlsList: 'nodownload noplaybackrate noremoteplayback',
        disablePictureInPicture: true,
        disableRemotePlayback: true,
        onContextMenu: blockSave,
      };

  const close = () => {
    onClose();
    setLoading(true);
    setFailed(false);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: '#0b0b12',
          overflow: 'hidden',
          borderRadius: fullScreen ? 0 : 2,
          // As wide as fits, but never taller than the screen allows for a 16:9 picture.
          width: fullScreen ? '100%' : `min(92vw, calc((90vh - ${HEADER_PX}px) * 16 / 9))`,
          m: fullScreen ? 0 : 2,
        },
      }}
    >
      <Box
        sx={{
          height: HEADER_PX,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#fff',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <PlayCircleIcon fontSize="small" sx={{ color: '#a78bfa' }} />
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, fontWeight: 600 }} title={title || undefined}>
          {title || 'Explanation video'}
        </Typography>
        <IconButton onClick={close} size="small" aria-label="Close video" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Students and parents may only play explanation videos here, never save them. */}
      <Box
        onContextMenu={blockSave}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          maxHeight: fullScreen ? `calc(100vh - ${HEADER_PX}px)` : undefined,
          bgcolor: '#000',
          mx: 'auto',
          ...(fullScreen && { flex: 1, aspectRatio: 'auto' }),
        }}
      >
        {open && videoUrl && (
          <video
            key={videoUrl}
            src={videoUrl}
            controls
            autoPlay
            playsInline
            {...lockProps}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            onLoadedData={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setFailed(true);
            }}
            title={title || 'Explanation video'}
          />
        )}
        {loading && !failed && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <CircularProgress sx={{ color: '#a78bfa' }} />
          </Box>
        )}
        {failed && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 3 }}>
            <Typography variant="body2" sx={{ color: '#e5e7eb', textAlign: 'center' }}>
              This video could not be loaded. Close it and try again in a moment.
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

const VideoExplanationPlayer = ({ videoUrl, questionText, variant = 'button', allowDownload = false }) => {
  const [open, setOpen] = useState(false);

  if (!videoUrl) return null;

  return (
    <>
      {variant === 'link' ? (
        <Typography
          component="span"
          onClick={() => setOpen(true)}
          variant="body2"
          color="primary"
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <PlayCircleIcon fontSize="small" />
          Watch explanation video
        </Typography>
      ) : (
        <IconButton onClick={() => setOpen(true)} color="primary" size="small" title="Watch explanation video">
          <PlayCircleIcon /> <Typography variant="caption" sx={{ ml: 0.5 }}>Watch Video</Typography>
        </IconButton>
      )}

      <VideoPlayerDialog
        open={open}
        onClose={() => setOpen(false)}
        videoUrl={videoUrl}
        title={questionText ? questionText.replace(/<[^>]*>/g, '').slice(0, 120) : null}
        allowDownload={allowDownload}
      />
    </>
  );
};

export default VideoExplanationPlayer;
